import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { Check, ListTodo, Plus } from 'lucide-react';
import { todoDueLabel, type TaskListItem, type WidgetSize } from '@tradeoutlet/shared';
import { api, errorMessage } from '@/lib/api';
import { useWorkspaceId } from '@/lib/hooks';
import { queryClient } from '@/lib/queryClient';
import { useUi } from '@/lib/store';
import { Button, Card, Input, Spinner } from '@/components/ui';
import { PrivateBadge, ukToday } from './privateTasks';

/** Overdue and today first, then by date (and time), then no date. */
export function sortMyTasks(list: TaskListItem[]): TaskListItem[] {
  return [...list].sort((a, b) => {
    if (!a.due_date !== !b.due_date) return a.due_date ? -1 : 1;
    const d = (a.due_date ?? '').localeCompare(b.due_date ?? '') || (a.due_time ?? '99').localeCompare(b.due_time ?? '99');
    return d || a.title.localeCompare(b.title);
  });
}

export const myTasksWidgetKey = (ws: string) => ['tasks-area', ws, { scope: 'mine', status: 'open' }] as const;

/** Home widget "My tasks" (was "My to-dos"): your open tasks, private ones marked, with a quick add for a private task. */
export function MyTasksWidget({ size }: { size: WidgetSize }) {
  const ws = useWorkspaceId();
  const toast = useUi(s => s.toast);
  const openTask = useUi(s => s.openTask);
  const q = useQuery({ queryKey: myTasksWidgetKey(ws ?? ''), queryFn: () => api.workspaceTasks(ws!, { scope: 'mine', status: 'open' }), enabled: Boolean(ws), select: d => sortMyTasks(d.tasks) });
  const [title, setTitle] = useState('');
  const ref = useRef<HTMLInputElement>(null);
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['tasks-area'] });
  const add = useMutation({
    mutationFn: (t: string) => api.createStandaloneTask(ws!, { title: t, is_private: true }),
    onSuccess: () => { setTitle(''); ref.current?.focus(); refresh(); }, onError: e => toast(errorMessage(e), 'error'),
  });
  const done = useMutation({
    mutationFn: (t: TaskListItem) => api.updateTask(t.id, { version: t.version, done: true }),
    onSuccess: (_r, t) => { refresh(); toast(`Done: ${t.title}`, 'success', { label: 'Undo', run: () => void api.task(t.id).then(x => api.updateTask(t.id, { version: x.version, done: false })).then(refresh) }); },
    onError: e => toast(errorMessage(e), 'error'),
  });
  const today = ukToday();
  const all = q.data ?? [];
  const list = all.slice(0, size === 's' ? 4 : size === 'm' ? 7 : 12);
  return (
    <div data-testid="widget-todos">
      <Card title={<span className="inline-flex items-center gap-2"><ListTodo className="h-4 w-4" />My tasks</span>} actions={<Link to="/tasks" className="link text-xs font-medium">Open</Link>}>
        <div className="space-y-3">
          <form className="flex gap-2" data-testid="private-quick-add" onSubmit={e => { e.preventDefault(); if (title.trim()) add.mutate(title.trim()); }}>
            <Input ref={ref} aria-label="New private task" placeholder="Add a private task… (Enter)" value={title} onChange={e => setTitle(e.target.value)} maxLength={300} />
            <Button type="submit" disabled={!title.trim()} loading={add.isPending} aria-label="Add private task"><Plus className="h-4 w-4" /></Button>
          </form>
          {q.isLoading ? <Spinner /> : list.length === 0 ? <p className="text-sm text-slate-500">Nothing to do.</p> : (
            <ul className="space-y-1.5">
              {list.map(t => {
                const due = t.due_date ? todoDueLabel({ due_date: t.due_date, due_time: t.due_time ?? null }, today) : null;
                return (
                  <li key={t.id} className="flex items-center gap-2 text-sm" data-testid="my-task-item">
                    <button type="button" role="checkbox" aria-checked={false} aria-label={`Mark “${t.title}” as done`} onClick={() => done.mutate(t)}
                      className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-transparent ring-2 ring-slate-300 hover:text-slate-400 hover:ring-slate-400 dark:ring-slate-600">
                      <Check className="h-3 w-3" strokeWidth={3} />
                    </button>
                    <button type="button" onClick={() => openTask(t.id)} className="min-w-0 flex-1 truncate text-left hover:underline">{t.title}</button>
                    {t.is_private && <PrivateBadge />}
                    {due && <span className={clsx('shrink-0 text-xs', t.due_date! < today ? 'font-medium text-rose-600 dark:text-rose-400' : 'text-slate-500')}>{due}</span>}
                  </li>
                );
              })}
            </ul>
          )}
          {all.length > list.length && <Link to="/tasks" className="link text-xs">{all.length - list.length} more</Link>}
        </div>
      </Card>
    </div>
  );
}
